/**
 * Renders a harness ``attachment`` block inside a project transcript
 * bubble.
 *
 * Opens attachments in the Work artifact canvas instead of ``window.open``,
 * so presigned URLs are minted only when the user previews — not embedded in
 * the bubble DOM. The BE TTL is ~1 hour; the canvas panel re-mints on open.
 */

import { FileCard } from '@heidi/design-system/file-card';
import { useSetAtom } from 'jotai';
import { FileImage } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Artifact } from '../../../data/projects/artifacts/state';
import { setOpenArtifactAtom } from '../../../data/projects/artifacts/state';
import { attachmentBlockRendererMessages } from './AttachmentBlockRenderer.messages';

import type { AttachmentBlock } from '../../../contracts/harness.types';

import {
  formatAttachmentType,
  formatBytes,
} from '../../../data/projects/attachmentFormatting';

interface AttachmentBlockRendererProps {
  attachment: AttachmentBlock;
  /** Durable run the attachment belongs to — scopes the download URL. */
  agentRunId: string;
}

const isPdfAttachment = (attachment: AttachmentBlock): boolean =>
  attachment.mime_type === 'application/pdf' ||
  attachment.filename.toLowerCase().endsWith('.pdf');

export const AttachmentBlockRenderer = ({
  attachment,
  agentRunId,
}: AttachmentBlockRendererProps): JSX.Element => {
  const intl = useIntl();
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const filename =
    attachment.filename ||
    intl.formatMessage({
      defaultMessage: 'file',
      id: 'hrYmyIZ6Bs',
      description: 'Fallback display name for an attachment without a filename',
    });
  const isImage = attachment.attachment_type === 'image';

  const fileTypeLabel = useMemo(() => {
    const ext = formatAttachmentType(attachment.filename);
    const size = formatBytes(attachment.size_bytes, intl);
    return size ? `${ext} · ${size}` : ext;
  }, [attachment.filename, attachment.size_bytes, intl]);

  const openInArtifact = useCallback(() => {
    const artifact: Artifact = {
      type: 'run-attachment',
      agentRunId,
      attachmentId: attachment.attachment_id,
      filename,
      ...(isPdfAttachment(attachment) ? { forcePdfExtension: true } : {}),
    };
    setOpenArtifact(artifact);
  }, [agentRunId, attachment, filename, setOpenArtifact]);

  if (isImage) {
    return (
      <button
        type="button"
        onClick={openInArtifact}
        aria-label={intl.formatMessage(attachmentBlockRendererMessages.previewAria, {
          filename,
        })}
        className="flex size-14 items-center justify-center rounded-lg border border-border bg-surface transition-colors hover:border-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <FileImage className="size-5 text-text-secondary" aria-hidden />
      </button>
    );
  }

  return (
    <FileCard
      fileName={filename}
      fileType={fileTypeLabel}
      size="default"
      onClick={openInArtifact}
    />
  );
};
